import type { ReactNode } from "react";
import { ApiError, ApiUnreachableError, refusedAction, complianceFailures } from "../api/client.js";
import type { AsyncState } from "../app/use-async.js";
import { IconState } from "./Icons.jsx";

/**
 * The three states of a screen that reads from the API, in one place.
 *
 * Written as a component rather than repeated per screen because the failure
 * branch is the one everybody skips, and a portal that renders a blank panel on
 * a 403 teaches its users that the software is broken when in fact it is
 * working exactly as the permission matrix says it should.
 */

export function Loading({ what }: { readonly what: string }): ReactNode {
  // `aria-live` on the region rather than a spinner: a screen reader should say
  // "loading the deadline board", not read an animation. The pulse is decorative
  // and `prefers-reduced-motion` stops it.
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="state__glyph" aria-hidden="true" />
      <p className="state__title">Loading {what}…</p>
    </div>
  );
}

/**
 * What went wrong, in the API's own words where it has any.
 *
 * The API distinguishes a refusal from a failure and says which grant it
 * refused; both are far more useful than "something went wrong", so both are
 * shown. A refusal deliberately offers no "Try again": retrying a 403 cannot
 * succeed, and a button that never works is worse than no button.
 */
export function Failure({
  error,
  what,
  reload,
}: {
  readonly error: unknown;
  readonly what: string;
  readonly reload?: () => void;
}): ReactNode {
  /*
   * NO ERROR IS NOT A FAILURE.
   *
   * Every branch below matches on what the error IS, and the last one catches
   * everything that matched nothing — including `null`, which is what
   * `useSubmit` holds before anything has been submitted. So a form that
   * rendered `<Failure error={save.error} …>` unconditionally showed
   * "… could not be loaded" from the moment it appeared, under a form that was
   * working perfectly. Sixty-six places render this and only sixteen guard it
   * themselves; the guard belongs here, once.
   */
  if (error === null || error === undefined) return null;

  if (error instanceof ApiUnreachableError) {
    return (
      <div className="state state--failed" role="alert">
        <span className="state__glyph">
          <IconState kind="offline" />
        </span>
        <p className="state__title">The API did not answer</p>
        <p className="state__body">
          {what} could not be loaded. This is a connection problem, not a
          refusal — nothing has been changed.
        </p>
        {reload ? (
          <button className="secondary" onClick={reload}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (error instanceof ApiError) {
    const refused = refusedAction(error);
    const failures = complianceFailures(error);

    if (refused) {
      return (
        <div className="state state--refused" role="alert">
          <span className="state__glyph">
            <IconState kind="refused" />
          </span>
          <p className="state__title">Not yours to see</p>
          <p className="state__body">
            Your role may not <strong>{refused.action}</strong>{" "}
            <strong>{refused.resource}</strong>, so {what} is not shown. This is
            a permission, not a fault: ask somebody with the grant, rather than
            trying again.
          </p>
        </div>
      );
    }

    if (failures.length > 0) {
      return (
        <div className="state state--failed" role="alert">
          <span className="state__glyph">
            <IconState kind="failed" />
          </span>
          <p className="state__title">The rules engine refused this:</p>
          <ul className="state__codes">
            {failures.map((failure) => (
              <li key={failure.code}>
                {failure.message} <span className="mono small">{failure.code}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div className="state state--failed" role="alert">
        <span className="state__glyph">
          <IconState kind="failed" />
        </span>
        <p className="state__body">
          {error.message} <span className="mono small">{error.code}</span>
        </p>
        {reload ? (
          <button className="secondary" onClick={reload}>
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="state state--failed" role="alert">
      <span className="state__glyph">
        <IconState kind="failed" />
      </span>
      <p className="state__body">{what} could not be loaded.</p>
      {reload ? (
        <button className="secondary" onClick={reload}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/**
 * Renders children only once the data is there, so a screen never has to guard
 * against `data` being null and TypeScript can prove it is not.
 */
export function Async<T>({
  state,
  what,
  reload,
  children,
}: {
  readonly state: AsyncState<T>;
  readonly what: string;
  readonly reload?: () => void;
  readonly children: (data: T) => ReactNode;
}): ReactNode {
  if (state.status === "loading") return <Loading what={what} />;
  if (state.status === "failed") {
    return <Failure error={state.error} what={what} reload={reload} />;
  }
  return <>{children(state.data)}</>;
}

/**
 * Things the screen could not find out, listed rather than hidden.
 *
 * A screen that reads more than one list — `Pipeline` reading jobs and
 * deadlines, `Tasks` reading tasks and the organisation's counts — passes
 * these when one source refused: a surveyor scoped to their own records gets a
 * 403 from an organisation-wide list, which changes what the rest of the list
 * means. Suppressing that would present a partial board as a complete one.
 */
export function Notices({ notes }: { readonly notes: readonly string[] }): ReactNode {
  if (notes.length === 0) return null;
  return (
    <div className="notice" role="note">
      <p className="small">
        <strong>What this list does not include</strong>
      </p>
      <ul className="small">
        {notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>
  );
}
